import type React from "react"
import { useState, useEffect, useMemo } from "react"
import { Modal, Table, Tag, Typography, Button, Spin, Alert, Input, Radio, Space, Tooltip } from "antd"
import {
  EyeOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons"
import type { ColumnsType } from "antd/es/table"

const { Text } = Typography

export interface PreviewItem {
  line_no?: number
  raw_text?: string
  text?: string
  status?: string
  meaning?: string | null
  part_of_speech?: string | null
  example?: string | null
  example_meaning?: string | null
  synonyms?: string | string[] | any[] | null
  synonym?: string | string[] | any[] | null
  antonyms?: string | string[] | any[] | null
  antonym?: string | string[] | any[] | null
  error_message?: string | null
}

export interface PreviewOutputData {
  total_items?: number
  items?: PreviewItem[]
}

export interface PreviewOutputModalProps {
  open: boolean
  outputPath?: string | null
  onClose: () => void
  record?: {
    id?: number
    user?: { name?: string; username?: string; email?: string }
    set?: { title?: string; name?: string }
    source_type?: string
    created_at?: string
  }
}

export const getDownloadUrl = (path: string) => {
  if (!path) return ""
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path.replace(/^http:\/\//i, "https://")
  }
  return `https://cdn.jupi.vn/${path.replace(/^\/+/, "")}`
}

const renderWordList = (data: any, tagColor: string) => {
  if (!data) return null
  let list: string[] = []
  if (Array.isArray(data)) {
    list = data
      .map((item) => (typeof item === "string" ? item : item.word || item.text || JSON.stringify(item)))
      .filter(Boolean)
  } else if (typeof data === "string" && data.trim()) {
    try {
      const parsed = JSON.parse(data)
      if (Array.isArray(parsed)) {
        list = parsed
          .map((item) => (typeof item === "string" ? item : item.word || item.text || JSON.stringify(item)))
          .filter(Boolean)
      } else {
        list = data
          .split(/[,;\n]+/)
          .map((s) => s.trim())
          .filter(Boolean)
      }
    } catch {
      list = data
        .split(/[,;\n]+/)
        .map((s) => s.trim())
        .filter(Boolean)
    }
  }
  if (list.length === 0) return null
  return (
    <div className="flex flex-wrap gap-1">
      {list.map((w, i) => (
        <Tag key={i} color={tagColor} className="m-0 px-1.5 py-0 text-[11px] leading-snug">
          {w}
        </Tag>
      ))}
    </div>
  )
}

export const PREVIEW_STATUS_CONFIG: Record<
  string,
  {
    label: string
    shortLabel: string
    color: string
    textColor: string
    icon: React.ReactNode
    description: string
  }
> = {
  valid_new: {
    label: "Hợp lệ",
    shortLabel: "Hợp lệ",
    color: "success",
    textColor: "text-emerald-600",
    icon: <CheckCircleOutlined />,
    description: "Hợp lệ (Sẵn sàng lưu): Tìm thấy nghĩa trong từ điển, sẵn sàng để lưu vào bộ.",
  },
  not_found: {
    label: "Không tìm thấy",
    shortLabel: "Không tìm thấy",
    color: "error",
    textColor: "text-rose-600",
    icon: <CloseCircleOutlined />,
    description:
      "Không tìm thấy: Không tìm thấy trong từ điển (gõ sai chính tả, từ lóng, file lỗi format...). Dấu hiệu drop-off chính.",
  },
  already_in_target_set: {
    label: "Đã có trong bộ",
    shortLabel: "Đã có trong bộ",
    color: "blue",
    textColor: "text-blue-600",
    icon: <InfoCircleOutlined />,
    description: "Đã có trong bộ: Từ này đã tồn tại trong bộ từ của người dùng.",
  },
  duplicate_in_input: {
    label: "Trùng lặp",
    shortLabel: "Trùng lặp",
    color: "warning",
    textColor: "text-amber-600",
    icon: <WarningOutlined />,
    description: "Trùng lặp: Bị lặp từ ngay trong danh sách/file vừa nhập.",
  },
}

export const normalizePreviewStatus = (status?: string): string => {
  const s = String(status || "")
    .trim()
    .toLowerCase()
  if (s === "valid_new" || s === "ready" || s === "valid" || s === "success") return "valid_new"
  if (s === "not_found" || s === "missing") return "not_found"
  if (s === "already_in_target_set" || s === "exists" || s === "already_exists") return "already_in_target_set"
  if (s === "duplicate_in_input" || s === "duplicate") return "duplicate_in_input"
  return s || "other"
}

export const PreviewOutputModal: React.FC<PreviewOutputModalProps> = ({ open, outputPath, onClose, record }) => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<PreviewOutputData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [searchText, setSearchText] = useState("")
  const [statusFilter, setStatusFilter] = useState<string>("all")

  const fileUrl = useMemo(() => (outputPath ? getDownloadUrl(outputPath) : ""), [outputPath])
  const fileName = useMemo(() => (outputPath ? outputPath.split("/").pop() || "preview_output.json" : ""), [outputPath])

  const fetchPreviewData = async () => {
    if (!fileUrl) return
    setLoading(true)
    setError(null)
    setData(null)

    try {
      const response = await fetch(fileUrl)
      if (!response.ok) {
        throw new Error(`Không thể tải file: HTTP ${response.status} (${response.statusText})`)
      }
      const json: PreviewOutputData = await response.json()
      setData(json)
    } catch (err: any) {
      setError(err?.message || "Đã xảy ra lỗi khi đọc file từ máy chủ CDN")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open && outputPath) {
      fetchPreviewData()
      setSearchText("")
      setStatusFilter("all")
    } else {
      setData(null)
      setError(null)
    }
  }, [open, outputPath])

  // Phân loại thống kê
  const stats = useMemo(() => {
    const items = data?.items || []
    let validNewCount = 0
    let notFoundCount = 0
    let alreadyInSetCount = 0
    let duplicateCount = 0
    let otherCount = 0

    items.forEach((item) => {
      const s = normalizePreviewStatus(item.status)
      if (s === "valid_new") validNewCount++
      else if (s === "not_found") notFoundCount++
      else if (s === "already_in_target_set") alreadyInSetCount++
      else if (s === "duplicate_in_input") duplicateCount++
      else otherCount++
    })

    return {
      total: data?.total_items ?? items.length,
      validNewCount,
      notFoundCount,
      alreadyInSetCount,
      duplicateCount,
      otherCount,
    }
  }, [data])

  // Lọc items theo search và status
  const filteredItems = useMemo(() => {
    let items = data?.items || []

    if (statusFilter !== "all") {
      items = items.filter((item) => {
        const s = normalizePreviewStatus(item.status)
        return s === statusFilter
      })
    }

    if (searchText.trim()) {
      const query = searchText.trim().toLowerCase()
      items = items.filter(
        (item) =>
          String(item.raw_text || item.text || "")
            .toLowerCase()
            .includes(query) ||
          String(item.meaning || "")
            .toLowerCase()
            .includes(query) ||
          String(item.example || "")
            .toLowerCase()
            .includes(query) ||
          String(item.example_meaning || "")
            .toLowerCase()
            .includes(query) ||
          String(JSON.stringify(item.synonyms || item.synonym || ""))
            .toLowerCase()
            .includes(query) ||
          String(JSON.stringify(item.antonyms || item.antonym || ""))
            .toLowerCase()
            .includes(query) ||
          String(item.error_message || "")
            .toLowerCase()
            .includes(query) ||
          String(item.part_of_speech || "")
            .toLowerCase()
            .includes(query)
      )
    }

    return items
  }, [data, statusFilter, searchText])

  const columns: ColumnsType<PreviewItem> = [
    {
      title: "# / Dòng",
      dataIndex: "line_no",
      width: 65,
      align: "center",
      render: (val, _, idx) => (
        <span className="font-mono text-gray-500 text-xs">{val !== undefined && val !== null ? val : idx + 1}</span>
      ),
    },
    {
      title: "Từ vựng (Gốc)",
      dataIndex: "raw_text",
      width: 140,
      render: (val, record) => (
        <span className="font-mono font-semibold text-gray-900 text-sm">{val || record.text || "—"}</span>
      ),
    },
    {
      title: "Từ loại",
      dataIndex: "part_of_speech",
      width: 90,
      align: "center",
      render: (val) => {
        if (!val) return <Text type="secondary">—</Text>
        return <Tag color="blue">{val}</Tag>
      },
    },
    {
      title: "Nghĩa tiếng Việt",
      dataIndex: "meaning",
      width: 180,
      render: (val) =>
        val ? (
          <span className="text-gray-800 text-sm leading-snug">{val}</span>
        ) : (
          <Text type="secondary" italic>
            Chưa có nghĩa
          </Text>
        ),
    },
    {
      title: "Ví dụ & Dịch nghĩa",
      key: "example",
      width: 260,
      render: (_, record) => {
        const example = record.example
        const exampleMeaning = record.example_meaning
        if (!example && !exampleMeaning) return <Text type="secondary">—</Text>

        return (
          <div className="flex flex-col gap-1 text-xs leading-relaxed">
            {example && (
              <div className="font-serif italic text-gray-800">
                “{example}”
              </div>
            )}
            {exampleMeaning && (
              <div className="text-[11px] text-gray-500">
                ↳ {exampleMeaning}
              </div>
            )}
          </div>
        )
      },
    },
    {
      title: "Đồng nghĩa & Trái nghĩa",
      key: "thesaurus",
      width: 220,
      render: (_, record) => {
        const syn = record.synonyms || record.synonym
        const ant = record.antonyms || record.antonym

        const renderedSyn = renderWordList(syn, "green")
        const renderedAnt = renderWordList(ant, "volcano")

        if (!renderedSyn && !renderedAnt) return <Text type="secondary">—</Text>

        return (
          <div className="flex flex-col gap-1.5 text-xs">
            {renderedSyn && (
              <div className="flex flex-col gap-0.5">
                <span className="font-semibold text-[11px] text-emerald-700">Đồng nghĩa:</span>
                {renderedSyn}
              </div>
            )}
            {renderedAnt && (
              <div className="flex flex-col gap-0.5">
                <span className="font-semibold text-[11px] text-rose-700">Trái nghĩa:</span>
                {renderedAnt}
              </div>
            )}
          </div>
        )
      },
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      width: 140,
      align: "center",
      render: (val) => {
        const key = normalizePreviewStatus(val)
        const config = PREVIEW_STATUS_CONFIG[key]
        if (config) {
          return (
            <Tooltip title={config.description} placement="top">
              <Tag color={config.color} icon={config.icon} className="cursor-help font-medium">
                {config.label}
              </Tag>
            </Tooltip>
          )
        }
        return <Tag>{val || "—"}</Tag>
      },
    },
    {
      title: "Ghi chú / Lỗi",
      dataIndex: "error_message",
      width: 140,
      render: (val) => {
        if (!val) return <Text type="secondary">—</Text>
        return (
          <Text type="danger" className="font-medium text-xs">
            {val}
          </Text>
        )
      },
    },
  ]

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={1280}
      title={
        <div className="flex items-center gap-2 pr-6">
          <EyeOutlined className="text-blue-500 text-lg" />
          <div className="flex flex-col">
            <span className="font-semibold text-base">Kết quả Xem trước (Preview Output)</span>
            {record && (
              <span className="font-normal text-gray-500 text-xs">
                Log #{record.id}
                {record.user?.name ? ` · Người dùng: ${record.user.name}` : ""}
                {record.set?.title ? ` · Bộ: ${record.set.title}` : ""}
              </span>
            )}
          </div>
        </div>
      }
      footer={
        <div className="flex items-center justify-between">
          <div className="max-w-md truncate text-left font-mono text-gray-500 text-xs">{outputPath}</div>
          <Space>
            {fileUrl && (
              <Button
                icon={<DownloadOutlined />}
                href={fileUrl}
                download={fileName}
                target="_blank"
                rel="noopener noreferrer"
              >
                Tải file gốc
              </Button>
            )}
            <Button type="primary" onClick={onClose}>
              Đóng
            </Button>
          </Space>
        </div>
      }
    >
      <div className="py-2">
        {loading && (
          <div className="flex flex-col items-center justify-center p-14">
            <Spin size="large" />
            <span className="mt-4 text-gray-500 text-sm">Đang tải dữ liệu kết quả Preview từ S3...</span>
          </div>
        )}

        {!loading && error && (
          <Alert
            type="error"
            showIcon
            message="Không thể đọc file Preview Output"
            description={
              <div className="mt-1 flex flex-col gap-2">
                <span>{error}</span>
                <span className="font-mono text-gray-500 text-xs">Đường dẫn: {fileUrl}</span>
                <div>
                  <Button size="small" icon={<ReloadOutlined />} onClick={fetchPreviewData}>
                    Thử lại
                  </Button>
                </div>
              </div>
            }
          />
        )}

        {!loading && !error && data && (
          <div className="flex flex-col gap-4">
            {/* Thanh thống kê tóm tắt */}
            <div className="grid grid-cols-2 gap-2 rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-center sm:grid-cols-5">
              <div className="flex flex-col items-center justify-center p-1">
                <span className="font-medium text-gray-500 text-xs">Tổng từ Preview</span>
                <span className="font-bold font-mono text-gray-800 text-lg">{stats.total}</span>
              </div>
              <Tooltip title="Tìm thấy nghĩa trong từ điển, sẵn sàng để lưu vào bộ">
                <div className="flex cursor-help flex-col items-center justify-center border-gray-200 border-l p-1">
                  <span className="font-medium text-emerald-600 text-xs">Hợp lệ</span>
                  <span className="font-bold font-mono text-emerald-600 text-lg">{stats.validNewCount}</span>
                </div>
              </Tooltip>
              <Tooltip title="Không tìm thấy trong từ điển (dấu hiệu drop-off chính)">
                <div className="flex cursor-help flex-col items-center justify-center border-gray-200 border-l p-1">
                  <span className="font-medium text-rose-600 text-xs">Không tìm thấy</span>
                  <span className="font-bold font-mono text-lg text-rose-600">{stats.notFoundCount}</span>
                </div>
              </Tooltip>
              <Tooltip title="Từ này đã tồn tại trong bộ từ của người dùng">
                <div className="flex cursor-help flex-col items-center justify-center border-gray-200 border-l p-1">
                  <span className="font-medium text-blue-600 text-xs">Đã có trong bộ</span>
                  <span className="font-bold font-mono text-blue-600 text-lg">{stats.alreadyInSetCount}</span>
                </div>
              </Tooltip>
              <Tooltip title="Bị lặp từ ngay trong danh sách/file vừa nhập">
                <div className="flex cursor-help flex-col items-center justify-center border-gray-200 border-l p-1">
                  <span className="font-medium text-amber-600 text-xs">Trùng lặp</span>
                  <span className="font-bold font-mono text-amber-600 text-lg">{stats.duplicateCount}</span>
                </div>
              </Tooltip>
            </div>

            {/* Bộ lọc và Tìm kiếm */}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Radio.Group
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                size="small"
                buttonStyle="solid"
              >
                <Radio.Button value="all">Tất cả ({stats.total})</Radio.Button>
                <Radio.Button value="valid_new">Hợp lệ ({stats.validNewCount})</Radio.Button>
                <Radio.Button value="not_found">Không tìm thấy ({stats.notFoundCount})</Radio.Button>
                <Radio.Button value="already_in_target_set">Đã có trong bộ ({stats.alreadyInSetCount})</Radio.Button>
                <Radio.Button value="duplicate_in_input">Trùng lặp ({stats.duplicateCount})</Radio.Button>
                {stats.otherCount > 0 && <Radio.Button value="other">Khác ({stats.otherCount})</Radio.Button>}
              </Radio.Group>

              <Input
                placeholder="Tìm từ vựng, nghĩa, lỗi..."
                prefix={<SearchOutlined />}
                size="small"
                allowClear
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: 240 }}
              />
            </div>

            {/* Bảng dữ liệu */}
            <Table
              rowKey={(record, idx) => `${record.line_no}_${idx}`}
              columns={columns}
              dataSource={filteredItems}
              size="small"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50", "100"],
                showTotal: (total) => `Tổng ${total} từ`,
              }}
              scroll={{ x: 1200, y: 440 }}
            />
          </div>
        )}
      </div>
    </Modal>
  )
}

export default PreviewOutputModal
